import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Upload, Building2, LogOut, X, Users, Cloud, UserCircle, CheckCircle2, ShieldCheck, BookOpen, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFicStatus } from "@/context/FicStatusContext";

// Per-scope navigation. For commercialisti we visually separate:
//   - CLIENT scope: things they do FOR the currently active client (invoices,
//     uploads, suppliers, integrations, dashboard triage).
//   - STUDIO scope: their own studio profile (account settings, credits,
//     billing, referral, docs).
const NAV_CLIENT = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/app/invoices", label: "Fatture", icon: FileText, testid: "nav-invoices" },
  { to: "/app/upload", label: "Carica PDF", icon: Upload, testid: "nav-upload" },
  { to: "/app/company", label: "Profilo cliente", icon: Building2, testid: "nav-company-client" },
  { to: "/app/suppliers", label: "Rubrica Fornitori", icon: Users, testid: "nav-suppliers" },
  { to: "/app/integrations", label: "Integrazioni", icon: Cloud, testid: "nav-integrations", ficBadge: true },
];
const NAV_COMPANY = { to: "/app/company", label: "Profilo azienda", icon: Building2, testid: "nav-company" };
const NAV_STUDIO = [
  { to: "/app/account", label: "Il tuo Account", icon: UserCircle, testid: "nav-account" },
  { to: "/docs", label: "Documentazione", icon: BookOpen, testid: "nav-docs", external: true },
];

export default function Sidebar({ open = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const { ready: ficReady, connected: ficConnected } = useFicStatus();
  const navigate = useNavigate();
  const isCommercialista = !!user?.is_commercialista;

  // Legacy flat nav for normal users (they don't need the studio/client split).
  // NAV_CLIENT contains a "Profilo cliente" entry pointing to /app/company —
  // for non-commercialisti that's a duplicate of NAV_COMPANY, so we filter it
  // out to avoid a React duplicate-key warning on `/app/company`.
  const NAV = isCommercialista
    ? null
    : [...NAV_CLIENT.filter((i) => i.to !== "/app/company"), NAV_COMPANY, ...NAV_STUDIO];

  const doLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={`
        w-64 shrink-0 border-r border-[#E8E4D9] bg-[#FAF9F6] flex flex-col h-screen
        fixed lg:sticky top-0 left-0 z-50 transform transition-transform duration-200 ease-out
        ${open ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0"} lg:shadow-none
      `}
    >
      <div className="px-5 py-6 border-b border-[#E8E4D9] flex items-center justify-between">
        <NavLink to="/" className="flex items-center gap-2">
          <div>
            <div className="font-display font-extrabold tracking-tight text-[#0A0A0A] leading-none">Autofattura</div>
            <div className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#737373] font-medium">
              by
              <img src="/logo.png" alt="Dasabo" className="h-3 w-auto" />
            </div>
          </div>
        </NavLink>
        <button
          type="button"
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-md hover:bg-white transition-colors"
          aria-label="Chiudi menu"
          data-testid="mobile-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {isCommercialista ? (
          <>
            <SectionLabel icon={Users} tone="blue">Gestione cliente</SectionLabel>
            {NAV_CLIENT.map((item) => (
              <SideItem key={item.to} item={item} onClose={onClose} ficReady={ficReady} ficConnected={ficConnected} tone="client" />
            ))}
            <div className="my-3 border-t border-[#E8E4D9]" />
            <SectionLabel icon={Briefcase} tone="purple">Il tuo studio</SectionLabel>
            {NAV_STUDIO.map((item) => (
              <SideItem key={item.to} item={item} onClose={onClose} ficReady={ficReady} ficConnected={ficConnected} tone="studio" />
            ))}
          </>
        ) : (
          NAV.map((item) => (
            <SideItem key={item.to} item={item} onClose={onClose} ficReady={ficReady} ficConnected={ficConnected} />
          ))
        )}

        {user?.is_admin && (
          <NavLink
            to="/app/admin"
            data-testid="nav-admin"
            onClick={onClose}
            className={({ isActive }) =>
              `side-item mt-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border ${
                isActive
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "text-emerald-800 bg-emerald-50/60 border-emerald-200 hover:bg-emerald-100"
              }`
            }
          >
            <ShieldCheck className="w-4 h-4" />
            <span className="flex-1">Admin console</span>
            <span
              title="Riservato agli amministratori"
              className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-200 text-[9px] font-bold uppercase tracking-wider"
            >
              Admin
            </span>
          </NavLink>
        )}
      </nav>

      <div className="border-t border-[#E8E4D9] p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#F97316] grid place-items-center text-white font-display font-bold text-sm shrink-0">
            {(user?.full_name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#0A0A0A] truncate" data-testid="sidebar-user-name">
              {user?.full_name}
            </div>
            <div className="text-xs text-[#737373] truncate" data-testid="sidebar-user-email">
              {user?.email}
            </div>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#4A4A4A] hover:bg-white hover:text-[#0A0A0A] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Esci
        </button>
      </div>
    </aside>
  );
}

function SectionLabel({ icon: Icon, tone, children }) {
  const cls = tone === "blue" ? "text-[#1D4ED8]" : tone === "purple" ? "text-[#6D28D9]" : "text-[#737373]";
  return (
    <div className={`px-3 pt-1 pb-2 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-bold ${cls}`}>
      <Icon className="w-3 h-3" />
      {children}
    </div>
  );
}

function SideItem({ item, onClose, ficReady, ficConnected, tone }) {
  const { to, label, icon: Icon, testid, end, ficBadge } = item;
  const activeCls = tone === "studio"
    ? "bg-[#6D28D9] text-white shadow-sm"
    : "bg-[#0A0A0A] text-white shadow-sm";
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      onClick={onClose}
      className={({ isActive }) =>
        `side-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? activeCls
            : "text-[#4A4A4A] hover:bg-white hover:text-[#0A0A0A]"
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {ficBadge && ficReady && (
        <span
          data-testid="sidebar-fic-badge-connected"
          title="Fatture in Cloud collegato"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold"
        >
          <CheckCircle2 className="w-2.5 h-2.5" />
        </span>
      )}
      {ficBadge && !ficReady && ficConnected && (
        <span
          data-testid="sidebar-fic-badge-pending"
          title="Seleziona un'azienda in Integrazioni"
          className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold"
        >
          !
        </span>
      )}
    </NavLink>
  );
}
